import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { getFirstExistingSnapshot } from '../../services/firebase';
import { DB_PATHS } from '../../services/schema';
import { normalizeProductsFromRTDB, normalizeCarouselFromRTDB, getTrendingProductsByWindow } from '../../utils/helpers';
import { getStats } from '../../services/firebase';

const hasUsableBannerPayload = (value) => {
  if (value == null) return false;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return false;

    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return hasUsableBannerPayload(JSON.parse(trimmed));
      } catch {
        return true;
      }
    }

    return true;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value !== 'object') return Boolean(value);

  if (typeof value.html === 'string' && value.html.trim()) return true;

  const nested =
    value.slides ??
    value.bannerSlides ??
    value.homeSlides ??
    value.heroSlides ??
    value.items ??
    value.banners ??
    value.banner ??
    value.homeBanner ??
    value.heroBanner;

  if (nested !== undefined && hasUsableBannerPayload(nested)) return true;

  return Boolean(
    value.image ||
    value.imageUrl ||
    value.url ||
    value.title ||
    value.subtitle ||
    value.badge ||
    value.buttonText ||
    value.action ||
    value.gradient ||
    value.lightGradient ||
    value.darkGradient ||
    value.gradientDark ||
    value.bannerGradient ||
    value.heroGradient
  );
};

const withDefaults = (product) => ({
  ...product,
  featured: typeof product.featured === 'boolean' ? product.featured : Math.random() > 0.7,
  popularity: typeof product.popularity === 'number' ? product.popularity : Math.floor(Math.random() * 100),
});

export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (_, { rejectWithValue }) => {
    try {
      const productSnapshot = await getFirstExistingSnapshot([DB_PATHS.PRODUCTS, 'products']);

      if (!productSnapshot) {
        return { premium: [], exclusive: [], bundle: [], addons: [], freeScripts: [], others: [] };
      }

      const normalizedList = normalizeProductsFromRTDB(productSnapshot.val());

      if (!normalizedList.length) {
        return { premium: [], exclusive: [], bundle: [], addons: [], freeScripts: [], others: [] };
      }

      const enriched = normalizedList.map(withDefaults);

      // Categorize products based on the database structure
      const premium = enriched.filter((item) =>
        (item.category || '').toLowerCase() === 'premium'
      );
      const exclusive = enriched.filter((item) =>
        (item.category || '').toLowerCase() === 'exclusive'
      );
      const bundle = enriched.filter((item) =>
        (item.category || '').toLowerCase() === 'bundle'
      );
      const addons = enriched.filter((item) =>
        (item.category || '').toLowerCase() === 'addon'
      );
      const freeScripts = enriched.filter((item) =>
        (item.category || '').toLowerCase() === 'free'
      );
      const others = enriched.filter((item) =>
        !['premium', 'exclusive', 'bundle', 'addon', 'free'].includes((item.category || '').toLowerCase())
      );

      return { premium, exclusive, bundle, addons, freeScripts, others };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch store-wide discounts
export const fetchStoreDiscounts = createAsyncThunk(
  'products/fetchStoreDiscounts',
  async (_, { rejectWithValue }) => {
    try {
      const snapshot = await getFirstExistingSnapshot([
        DB_PATHS.ADMIN_SETTINGS + '/storeDiscount',
        'Admin/settings/storDiscount', // Legacy typo support
        'Admin/storeDiscount',
        'admin/storeDiscount',
        'Admin/storeDiscounts',
        'admin/storeDiscounts',
        'Admin/StoreDiscount',
        'Admin/StoreDiscounts',
        'storeDiscount',
        'storeDiscounts'
      ]);
      if (snapshot && snapshot.exists()) {
        let val = snapshot.val();
        // If the data is stored as a string (serialized JSON), parse it.
        if (typeof val === 'string') {
          try {
            val = JSON.parse(val);
          } catch (e) {
            console.error('Failed to parse store discounts string:', e);
            val = {};
          }
        }
        // console.log('Fetched Store Discounts:', val);
        return val;
      }
      console.log('No Store Discounts found in DB at checked paths');
      // Temporary: Log that we are failing so I know for sure
      return {};
    } catch (error) {
      console.error('Error fetching store discounts:', error);
      return {};
    }
  }
);

export const fetchCarouselImages = createAsyncThunk(
  'products/fetchCarouselImages',
  async (_, { rejectWithValue }) => {
    try {
      const snapshot = await getFirstExistingSnapshot([DB_PATHS.POPUPS, 'Admin/carouselImages', 'admin/carouselImages']);

      if (snapshot && snapshot.exists()) {
        const images = normalizeCarouselFromRTDB(snapshot.val());
        return images.length > 0 ? images : [];
      }

      return [];
    } catch (error) {
      return [];
    }
  }
);

export const fetchBannerHtml = createAsyncThunk(
  'products/fetchBannerHtml',
  async (_, { rejectWithValue }) => {
    try {
      const snapshot = await getFirstExistingSnapshot([DB_PATHS.BANNERS, 'Admin/bannerHtml', 'admin/bannerHtml']);

      if (snapshot && snapshot.exists()) {
        const value = snapshot.val();
        if (hasUsableBannerPayload(value)) {
          return value;
        }
      }

      const settingsSnapshot = await getFirstExistingSnapshot([DB_PATHS.ADMIN_SETTINGS, 'admin/settings']);
      const settings = settingsSnapshot?.exists() ? settingsSnapshot.val() : null;
      const settingsBanner =
        settings?.bannerHtml ??
        settings?.homeBanner ??
        settings?.heroBanner ??
        settings?.banner ??
        settings?.banners ??
        settings?.bannerSlides ??
        settings?.slides ??
        null;

      if (settingsBanner) {
        return settingsBanner;
      }

      const settingsGradient =
        settings?.bannerGradient ??
        settings?.heroGradient ??
        settings?.homeBannerGradient ??
        settings?.gradient ??
        settings?.lightGradient ??
        settings?.backgroundGradient ??
        '';
      const settingsDarkGradient =
        settings?.bannerDarkGradient ??
        settings?.heroDarkGradient ??
        settings?.homeBannerDarkGradient ??
        settings?.darkGradient ??
        settings?.gradientDark ??
        settings?.darkBackgroundGradient ??
        '';

      if (settingsGradient || settingsDarkGradient) {
        return {
          gradient: settingsGradient,
          darkGradient: settingsDarkGradient,
        };
      }

      return null;
    } catch (error) {
      return null;
    }
  }
);

// Fetch trending products based on sales data
export const fetchTrendingProducts = createAsyncThunk(
  'products/fetchTrendingProducts',
  async (windowDays = 0, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { premium, exclusive, bundle, addons = [], freeScripts, others } = state.products;

      // If we don't have products yet, fetch them first
      let allProducts = [...premium, ...exclusive, ...bundle, ...addons, ...freeScripts, ...others];
      if (allProducts.length === 0) {
        const productSnapshot = await getFirstExistingSnapshot(['Products', 'products']);
        if (productSnapshot) {
          const normalizedList = normalizeProductsFromRTDB(productSnapshot.val());
          allProducts = normalizedList.map(withDefaults);
        }
      }

      // Fetch stats data
      const statsData = await getStats();

      // Get trending products based on sales
      const trendingProducts = getTrendingProductsByWindow(statsData, allProducts, windowDays);

      return { items: trendingProducts, windowDays };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const productsSlice = createSlice({
  name: 'products',
  initialState: {
    premium: [],
    exclusive: [],
    bundle: [],
    addons: [],
    freeScripts: [],
    others: [],
    carouselImages: [],
    carouselLoaded: false,
    storeDiscounts: {}, // New state for discounts
    bannerHtml: null,
    trending: [],
    trendingWindowDays: 7,
    loading: false,
    trendingLoading: false,
    error: null,
    trendingError: null,
    searchQuery: '',
    selectedCategory: 'all',
    sortBy: 'newest',
    displayedCount: 24,
    scrollPosition: 0,
  },
  reducers: {
    setScrollPosition: (state, action) => {
      state.scrollPosition = action.payload;
    },
    setDisplayedCount: (state, action) => {
      state.displayedCount = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.premium = action.payload.premium;
        state.exclusive = action.payload.exclusive;
        state.bundle = action.payload.bundle;
        state.addons = action.payload.addons || [];
        state.freeScripts = action.payload.freeScripts;
        state.others = action.payload.others;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCarouselImages.fulfilled, (state, action) => {
        state.carouselImages = action.payload;
        state.carouselLoaded = true;
      })
      .addCase(fetchStoreDiscounts.fulfilled, (state, action) => {
        state.storeDiscounts = action.payload;
      })
      .addCase(fetchBannerHtml.fulfilled, (state, action) => {
        state.bannerHtml = action.payload;
      })
      .addCase(fetchTrendingProducts.pending, (state) => {
        state.trendingLoading = true;
        state.trendingError = null;
      })
      .addCase(fetchTrendingProducts.fulfilled, (state, action) => {
        state.trendingLoading = false;
        state.trending = action.payload?.items || [];
        state.trendingWindowDays = Number.isFinite(Number(action.payload?.windowDays))
          ? Number(action.payload.windowDays)
          : 0;
      })
      .addCase(fetchTrendingProducts.rejected, (state, action) => {
        state.trendingLoading = false;
        state.trendingError = action.payload;
      });
  },
});

export const { setSearchQuery, setSelectedCategory, setSortBy, clearError, setDisplayedCount, setScrollPosition } = productsSlice.actions;

// MEMOIZED SELECTORS
export const selectAllProducts = createSelector(
  [
    (state) => state.products.premium,
    (state) => state.products.exclusive,
    (state) => state.products.bundle,
    (state) => state.products.addons || [],
    (state) => state.products.freeScripts,
    (state) => state.products.others
  ],
  (premium, exclusive, bundle, addons, freeScripts, others) => [...premium, ...exclusive, ...bundle, ...addons, ...freeScripts, ...others]
);

export const selectProductsByCategory = createSelector(
  [selectAllProducts, (state, category) => category],
  (allProducts, category) => {
    const publicProducts = allProducts.filter(
      (product) => String(product?.status || '').trim().toLowerCase() !== 'private'
    );
    if (category === 'all') return publicProducts;
    return publicProducts.filter(product => product.category === category);
  }
);

export const selectFilteredProducts = createSelector(
  [
    selectAllProducts,
    (state) => state.products.searchQuery,
    (state) => state.products.selectedCategory,
    (state) => state.products.sortBy,
  ],
  (allProducts, searchQuery, selectedCategory, sortBy) => {
    // Hide private products from public catalog browsing & search
    let filtered = allProducts.filter(
      (product) => String(product?.status || '').trim().toLowerCase() !== 'private'
    );

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(query) ||
        product.description.toLowerCase().includes(query) ||
        product.category.toLowerCase().includes(query)
      );
    }

    // Sort products - create new array to avoid mutation
    const sortedFiltered = [...filtered];
    switch (sortBy) {
      case 'featured':
        sortedFiltered.sort((a, b) => {
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          return b.popularity - a.popularity;
        });
        break;
      case 'price-low':
        sortedFiltered.sort((a, b) => {
          const priceA = extractNumericPrice(a.price);
          const priceB = extractNumericPrice(b.price);
          return priceA - priceB;
        });
        break;
      case 'price-high':
        sortedFiltered.sort((a, b) => {
          const priceA = extractNumericPrice(a.price);
          const priceB = extractNumericPrice(b.price);
          return priceB - priceA;
        });
        break;
      case 'newest':
        sortedFiltered.sort((a, b) => {
          // Use updatedAt if available, otherwise fall back to createdAt
          const timeA = a.updatedAt || a.createdAt || 0;
          const timeB = b.updatedAt || b.createdAt || 0;
          return timeB - timeA;
        });
        break;
      case 'name':
        sortedFiltered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }

    return sortedFiltered;
  }
);

// Updated selector to use sales-based trending data
export const selectTrendingProducts = createSelector(
  [(state) => state.products.trending, selectAllProducts],
  (trending, allProducts) => {
    const isPublic = (product) => String(product?.status || '').trim().toLowerCase() !== 'private';
    // If we have sales-based trending data, use it (excluding private products)
    if (trending && trending.length > 0) {
      return trending.filter(isPublic);
    }

    // Fallback to old logic if no sales data - prioritize newest products
    return allProducts
      .filter(isPublic)
      .filter(product => product.category !== 'bundle')
      .sort((a, b) => {
        // Use updatedAt if available, otherwise fall back to createdAt
        const timeA = a.updatedAt || a.createdAt || 0;
        const timeB = b.updatedAt || b.createdAt || 0;
        const scoreA = (a.featured ? 1000000 : 0) + (a.popularity || 0) * 1000 + timeA;
        const scoreB = (b.featured ? 1000000 : 0) + (b.popularity || 0) * 1000 + timeB;
        return scoreB - scoreA;
      })
      .slice(0, 4);
  }
);

// New selectors for trending
export const selectTrendingLoading = (state) => state.products.trendingLoading;
export const selectTrendingError = (state) => state.products.trendingError;
export const selectTrendingWindowDays = (state) => state.products.trendingWindowDays;
export const selectStoreDiscounts = (state) => state.products.storeDiscounts;

// Helper function to extract numeric price
const extractNumericPrice = (price) => {
  if (typeof price === 'number') return price;
  if (typeof price === 'string') {
    const match = price.match(/[\d.]+/);
    return match ? parseFloat(match[0]) : 0;
  }
  return 0;
};

export default productsSlice.reducer;
